import type { MouseEvent } from 'react';

/**
 * Button handlers for actions that must run on `mousedown` (before a composer
 * blur/shrink can steal a subsequent click). Keyboard activation still uses
 * `click` with `detail === 0`.
 */
function isModifiedClick(event: MouseEvent) {
  return event.metaKey || event.ctrlKey || event.shiftKey || event.altKey;
}

export function composerSafeButtonHandlers(onActivate: () => void) {
  return {
    onMouseDown: (event: MouseEvent<HTMLButtonElement>) => {
      if (event.button === 0) {
        onActivate();
        // Browsers focus the button on mousedown (blurring the composer); jsdom does not.
        event.currentTarget.focus();
      }
    },
    onClick: (event: MouseEvent<HTMLButtonElement>) => {
      if (event.detail === 0) {
        onActivate();
      }
    },
  };
}

/**
 * Same timing as {@link composerSafeButtonHandlers}, but for in-app `<a>`
 * clicks. Modifier clicks stay native so the browser can open a new tab.
 */
export function composerSafeAnchorHandlers(onActivate: () => void) {
  return {
    onMouseDown: (event: MouseEvent<HTMLAnchorElement>) => {
      if (event.button !== 0 || isModifiedClick(event)) {
        return;
      }

      event.preventDefault();
      onActivate();
      event.currentTarget.focus();
    },
    onClick: (event: MouseEvent<HTMLAnchorElement>) => {
      if (isModifiedClick(event)) {
        return;
      }

      event.preventDefault();
      if (event.detail === 0) {
        onActivate();
      }
    },
  };
}
